import { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    MessageCircle, Send, ImagePlus, Search, Loader2, Plus, Trash2,
    ChevronLeft, ChevronDown, ChevronUp,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
const POLL_MS = 8000; // 8s (was 2s — was causing the whole conversation
                      //     list + thread to visibly re-render every ~2s,
                      //     which admins reported as a jittery "screen keeps
                      //     jumping" bug on production, Feb 2026. 8s is still
                      //     effectively real-time for support chat.)

const fmtTime = (iso) => {
    try {
        const d = new Date(iso);
        const today = new Date();
        if (d.toDateString() === today.toDateString()) {
            return d.toLocaleTimeString('en-US', { timeZone: 'America/Chicago', hour: 'numeric', minute: '2-digit' });
        }
        return d.toLocaleDateString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric' });
    } catch { return ''; }
};

export const SupportAdminTab = () => {
    const [convos, setConvos] = useState([]);
    const [search, setSearch] = useState('');
    const [onlyUnread, setOnlyUnread] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [thread, setThread] = useState([]);
    const [selectedConvo, setSelectedConvo] = useState(null);
    const [text, setText] = useState('');
    const [pendingImage, setPendingImage] = useState(null);
    const [sending, setSending] = useState(false);
    const [canned, setCanned] = useState([]);
    const [showCanned, setShowCanned] = useState(true);
    const [newCanned, setNewCanned] = useState({ label: '', body: '' });
    const scrollRef = useRef(null);
    const fileRef = useRef(null);

    const loadConvos = useCallback(async () => {
        try {
            const params = {};
            if (search.trim()) params.q = search.trim();
            if (onlyUnread) params.only_unread = true;
            const { data } = await axios.get(`${API}/admin/support/conversations`, { params });
            // Only setState when the payload actually changed — a shallow
            // JSON compare is cheap and prevents the whole list from
            // re-rendering + flickering on every poll when nothing new
            // arrived. This is what was making the screen "jump" every 2s.
            const next = data.conversations || [];
            setConvos((prev) => {
                if (prev.length === next.length) {
                    try {
                        const a = JSON.stringify(prev);
                        const b = JSON.stringify(next);
                        if (a === b) return prev;
                    } catch { /* fall through to update */ }
                }
                return next;
            });
        } catch (e) { /* ignore */ }
    }, [search, onlyUnread]);

    const loadThread = useCallback(async (cid, silent = false) => {
        if (!cid) return;
        try {
            const { data } = await axios.get(`${API}/admin/support/conversations/${cid}`);
            const next = data.messages || [];
            // Same JSON-compare guard as loadConvos — no-op setState if the
            // thread hasn't changed. Keeps the message list stable between
            // polls when there's nothing new.
            setThread((prev) => {
                if (prev.length === next.length) {
                    try {
                        const a = JSON.stringify(prev);
                        const b = JSON.stringify(next);
                        if (a === b) return prev;
                    } catch { /* fall through */ }
                }
                return next;
            });
            setSelectedConvo(data.conversation);
            if (!silent) {
                setTimeout(() => {
                    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
                }, 50);
            }
        } catch (e) { /* ignore */ }
    }, []);

    const loadCanned = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/admin/support/canned-replies`);
            setCanned(data || []);
        } catch (e) { /* ignore */ }
    }, []);

    // Initial + conversation list polling. Skips ticks when the tab is
    // backgrounded so we don't waste requests + battery.
    useEffect(() => {
        loadConvos();
        loadCanned();
        const tick = () => { if (!document.hidden) loadConvos(); };
        const id = setInterval(tick, POLL_MS * 1.5);
        return () => clearInterval(id);
    }, [loadConvos, loadCanned]);

    // Deep-open a specific conversation from the support pop-up toast.
    useEffect(() => {
        const onOpen = (e) => { if (e?.detail) setSelectedId(e.detail); };
        window.addEventListener('admin:open-support-convo', onOpen);
        return () => window.removeEventListener('admin:open-support-convo', onOpen);
    }, []);


    // Thread polling when a conversation is open — also skips when hidden.
    useEffect(() => {
        if (!selectedId) return undefined;
        loadThread(selectedId, false);
        // mark as read when opened
        axios.post(`${API}/admin/support/conversations/${selectedId}/mark-read`).catch(() => {});
        const tick = () => { if (!document.hidden) loadThread(selectedId, true); };
        const id = setInterval(tick, POLL_MS);
        return () => clearInterval(id);
    }, [selectedId, loadThread]);

    const pickImage = () => fileRef.current && fileRef.current.click();
    const onFile = (e) => {
        const f = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!f) return;
        if (f.size > MAX_IMAGE_BYTES) { toast.error(`Image too big. Max 2MB.`); return; }
        const r = new FileReader();
        r.onload = () => setPendingImage(r.result);
        r.readAsDataURL(f);
    };

    const send = async () => {
        if (!selectedId) return;
        const body = text.trim();
        if (!body && !pendingImage) return;
        setSending(true);
        try {
            const { data } = await axios.post(`${API}/admin/support/conversations/${selectedId}/message`, {
                text: body || null, image_data_url: pendingImage,
            });
            setThread(prev => [...prev, data]);
            setText('');
            setPendingImage(null);
            setTimeout(() => {
                if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
            }, 50);
            loadConvos();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to send');
        } finally {
            setSending(false);
        }
    };

    const onKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
    };

    const saveCanned = async () => {
        const label = newCanned.label.trim();
        const body = newCanned.body.trim();
        if (!label || !body) { toast.error('Both label and body are required'); return; }
        try {
            await axios.post(`${API}/admin/support/canned-replies`, { label, body });
            setNewCanned({ label: '', body: '' });
            toast.success('Quick reply saved');
            loadCanned();
        } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    };

    const deleteCanned = async (id) => {
        if (!window.confirm('Delete this quick reply?')) return;
        try {
            await axios.delete(`${API}/admin/support/canned-replies/${id}`);
            loadCanned();
        } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    };

    const applyCannedReply = (body) => {
        setText(prev => prev ? `${prev}\n${body}` : body);
    };

    return (
        <div className="space-y-4" data-testid="support-admin-tab">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-[#FFD700]" />
                    Live Support Inbox
                </h3>
                <span className="text-[10px] text-[#A0AAB2]">Polling every 2s · conversations every 3s</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[300px_1fr] gap-3 h-[640px]">
                {/* Conversation list */}
                <div className="card-dark flex flex-col overflow-hidden">
                    <div className="p-2.5 border-b border-white/5 space-y-2">
                        <div className="relative">
                            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#A0AAB2]" />
                            <Input value={search} onChange={(e) => setSearch(e.target.value)}
                                   placeholder="Search email, name, text…"
                                   className="h-8 pl-8 bg-[#0a0a0a] border-white/10 text-white text-[11px]"
                                   data-testid="support-convo-search" />
                        </div>
                        <label className="flex items-center gap-2 text-[10px] text-[#A0AAB2] cursor-pointer select-none">
                            <input type="checkbox" checked={onlyUnread}
                                   onChange={(e) => setOnlyUnread(e.target.checked)}
                                   className="accent-[#FFD700]"
                                   data-testid="support-only-unread" />
                            Show unread only
                        </label>
                    </div>
                    <div className="flex-1 overflow-y-auto" data-testid="support-convo-list">
                        {convos.length === 0 ? (
                            <div className="p-6 text-center text-[11px] text-[#A0AAB2]">
                                No conversations {onlyUnread ? 'with unread messages' : 'yet'}.
                            </div>
                        ) : (
                            convos.map(c => (
                                <button key={c.id} onClick={() => setSelectedId(c.id)}
                                    className={`w-full text-left p-2.5 border-b border-white/5 hover:bg-white/[0.04] transition-colors ${selectedId === c.id ? 'bg-[#FFD700]/10' : ''}`}
                                    data-testid={`support-convo-${c.id}`}>
                                    <div className="flex items-start justify-between gap-1 mb-0.5">
                                        <span className="text-[12px] font-bold text-white truncate">{c.user_name || c.user_email}</span>
                                        <span className="text-[9px] text-[#A0AAB2] flex-shrink-0">{fmtTime(c.last_message_at)}</span>
                                    </div>
                                    <div className="flex items-center justify-between gap-2">
                                        <p className="text-[10px] text-[#A0AAB2] truncate flex-1">
                                            {c.last_sender_role === 'admin' && <span className="text-[#FFD700] font-bold">You: </span>}
                                            {c.last_message_preview || '(empty)'}
                                        </p>
                                        {c.unread_for_admin > 0 && (
                                            <span className="px-1.5 py-0.5 rounded-full bg-[#FF3B30] text-white text-[9px] font-black animate-pulse"
                                                  data-testid={`support-convo-unread-${c.id}`}>
                                                {c.unread_for_admin}
                                            </span>
                                        )}
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                </div>

                {/* Active conversation */}
                <div className="card-dark flex flex-col overflow-hidden">
                    {!selectedId || !selectedConvo ? (
                        <div className="flex-1 flex items-center justify-center p-8">
                            <div className="text-center">
                                <MessageCircle className="w-10 h-10 mx-auto mb-2 text-[#FFD700]/30" />
                                <p className="text-[12px] text-[#A0AAB2]">Select a conversation to start replying.</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Thread header */}
                            <div className="flex items-center justify-between p-3 border-b border-white/5">
                                <div className="flex items-center gap-2">
                                    <button onClick={() => { setSelectedId(null); setThread([]); setSelectedConvo(null); }}
                                            className="md:hidden w-7 h-7 rounded hover:bg-white/10 flex items-center justify-center"
                                            data-testid="support-back-btn">
                                        <ChevronLeft className="w-4 h-4 text-white" />
                                    </button>
                                    <div>
                                        <p className="text-[13px] font-bold text-white">{selectedConvo.user_name || selectedConvo.user_email}</p>
                                        <p className="text-[10px] text-[#A0AAB2]">{selectedConvo.user_email} · {selectedConvo.total_messages} total messages</p>
                                    </div>
                                </div>
                            </div>

                            {/* Messages */}
                            <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2"
                                 data-testid="support-thread-messages">
                                {thread.length === 0 ? (
                                    <div className="text-center py-10 text-[#A0AAB2] text-[12px]">No messages in this thread yet.</div>
                                ) : (
                                    thread.map(m => <AdminMessageBubble key={m.id} message={m} />)
                                )}
                            </div>

                            {/* Canned replies */}
                            <div className="border-t border-white/5">
                                <button
                                    onClick={() => setShowCanned(!showCanned)}
                                    className="w-full px-3 py-1.5 flex items-center justify-between text-[10px] text-[#A0AAB2] hover:bg-white/[0.03]"
                                    data-testid="support-canned-toggle"
                                >
                                    <span className="font-bold uppercase tracking-wider">Quick Replies ({canned.length})</span>
                                    {showCanned ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
                                </button>
                                {showCanned && (
                                    <div className="px-3 pb-2 space-y-1.5">
                                        <div className="flex flex-wrap gap-1.5">
                                            {canned.map(c => (
                                                <button
                                                    key={c.id}
                                                    onClick={() => applyCannedReply(c.body)}
                                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-[#FFD700]/10 border border-[#FFD700]/30 text-[#FFD700] hover:bg-[#FFD700]/20"
                                                    title={c.body}
                                                    data-testid={`support-canned-${c.id}`}
                                                >
                                                    {c.label}
                                                    <Trash2
                                                        className="w-2.5 h-2.5 text-[#FF3B30] hover:scale-110"
                                                        onClick={(e) => { e.stopPropagation(); deleteCanned(c.id); }}
                                                    />
                                                </button>
                                            ))}
                                        </div>
                                        <div className="flex gap-1.5">
                                            <Input value={newCanned.label}
                                                   onChange={(e) => setNewCanned(v => ({ ...v, label: e.target.value }))}
                                                   placeholder="Label" className="h-7 text-[10px] bg-[#0a0a0a] border-white/10 text-white w-28"
                                                   data-testid="support-canned-label" />
                                            <Input value={newCanned.body}
                                                   onChange={(e) => setNewCanned(v => ({ ...v, body: e.target.value }))}
                                                   placeholder="Full reply text…" className="h-7 text-[10px] bg-[#0a0a0a] border-white/10 text-white flex-1"
                                                   data-testid="support-canned-body" />
                                            <Button size="sm" onClick={saveCanned}
                                                    className="h-7 px-2 bg-[#39FF14] text-black hover:bg-[#6bff55] text-[10px]"
                                                    data-testid="support-canned-save">
                                                <Plus className="w-3 h-3" />
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Image preview */}
                            {pendingImage && (
                                <div className="px-3 py-2 flex items-center gap-2 border-t border-white/5">
                                    <img src={pendingImage} alt="" className="h-10 w-10 object-cover rounded border border-white/10" />
                                    <span className="text-[10px] text-[#A0AAB2] flex-1">Image ready to send</span>
                                    <button onClick={() => setPendingImage(null)} className="text-[10px] text-[#FF3B30] font-bold">Remove</button>
                                </div>
                            )}

                            {/* Composer */}
                            <div className="flex items-end gap-1.5 p-2.5 border-t border-white/5 bg-[#121212]">
                                <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden"
                                       data-testid="support-admin-file" />
                                <button onClick={pickImage} disabled={sending}
                                        className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-[#A0AAB2]"
                                        title="Attach image" data-testid="support-admin-attach">
                                    <ImagePlus className="w-4 h-4" />
                                </button>
                                <textarea value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKeyDown}
                                          placeholder="Type a reply…" rows={1}
                                          className="flex-1 resize-none bg-[#0a0a0a] border border-white/10 rounded-xl px-3 py-2 text-[12px] text-white placeholder:text-[#555] focus:outline-none focus:border-[#FFD700]/50 max-h-24"
                                          data-testid="support-admin-input" />
                                <Button onClick={send} disabled={sending || (!text.trim() && !pendingImage)} size="sm"
                                        className="h-9 px-3 rounded-full bg-gradient-to-r from-[#FFD700] to-[#FF8C00] text-black font-black disabled:opacity-40"
                                        data-testid="support-admin-send">
                                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

const AdminMessageBubble = ({ message }) => {
    const mine = message.sender_role === 'admin';
    const ts = (() => {
        try { return new Date(message.created_at).toLocaleTimeString('en-US', { timeZone: 'America/Chicago', hour: 'numeric', minute: '2-digit' }); }
        catch { return ''; }
    })();
    return (
        <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
            <div
                className="max-w-[80%] rounded-xl px-3 py-2"
                style={{
                    background: mine ? 'rgba(255,215,0,0.15)' : 'rgba(255,255,255,0.05)',
                    border: mine ? '1px solid rgba(255,215,0,0.3)' : '1px solid rgba(255,255,255,0.08)',
                }}
            >
                {!mine && (
                    <p className="text-[9px] font-bold text-[#00BFFF] mb-0.5 uppercase tracking-wider">
                        {message.sender_name || 'Customer'}
                    </p>
                )}
                {message.image_data_url && (
                    <img src={message.image_data_url} alt="" className="max-w-full rounded mb-1 border border-white/10"
                         style={{ maxHeight: 240 }} />
                )}
                {message.text && (
                    <p className="text-[12px] text-white whitespace-pre-wrap leading-snug break-words">{message.text}</p>
                )}
                <p className="text-[9px] text-[#666] mt-1 text-right">{ts}</p>
            </div>
        </div>
    );
};

export default SupportAdminTab;
